import { useEffect, useState, useCallback } from "react";
import axios from "../customAxios.js";

/**
 * Custom Hook to obtain and manage user plan data
 *
 * Make a call to the API to get the information from the associated plan,
 * automatically managing the loading and error states
 * The request runs once when the hook is mounted
 *
 * @returns {{
 * plan: object,
 * loading: boolean,
 * error: string | null,
 * setLoading: React.Dispatch<React.SetStateAction<boolean>>,
 * setError: React.Dispatch<React.SetStateAction<string | null>>
 * }} An object that contains plan data, loading and error states, and its update functions
 *
 * @example
 * const { plan, loading, error } = usePlan();
 */

export function usePlan() {
  // Data status and IU
  const [plan, setPlan] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // STATE MANNERS (HANDLERS)
  const getPlan = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const {
        data: plan,
      } = await axios.get("get_plan/");
      setPlan(plan);
    } catch (error) {
      console.error(`No se pudo obtener los datos del plan: ${error}`);
      setError("No se pudo obtener los datos del plan");
    } finally {
      setLoading(false);
    }
  }, []);

  // SECONDARY EFFECTS (FETCH, DEBUNCE)
  useEffect(() => {
    getPlan();
  }, [getPlan]);

  // PUBLIC API OF THE HOOK
  return {
    // State and Data
    plan,
    loading,
    error,

    // MANNERS (Setters y Actions)
    setLoading,
    setError,
  };
}
